import { Component, OnDestroy, OnInit } from '@angular/core';
import { RootState } from '@next/core-lib/app';
import { FilterAction } from '@next/core-lib/components';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { SettingsService, SettingsStorage } from '@next/core-lib/settings';
import { UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import {
  DeliveryCategories,
  DeliveryColumnTypes,
} from '../../../delivery-data/delivery-data.model';
import { DeliveryDataService } from '../../../delivery-data/services/delivery-data.service';
import {
  clearDeliveryTotalsRequest,
  printDeliveryReportRequest,
  loadDeliveryTotalsRequest,
} from '../../../delivery-data/store/delivery-data.actions';
import { selectDeliveryTotals } from '../../../delivery-data/store/delivery-data.selectors';
import { HierarchicalData } from '../../../shared/hierarchical-data/hierarchical-data.model';
import { settingsContextName, SettingsKeys } from '../../../shared/models/shared.models';
import { PageHierarchicalDataFilterComponent } from '../../../shared/page-hierarchical-data-filter/page-hierarchical-data-filter.component';

@Component({
  selector: 'app-gbm-totals',
  templateUrl: './gbm-totals.component.html',
  imports: [PageHierarchicalDataFilterComponent, TranslatePipe],
})
export class GbmTotalsComponent implements OnInit, OnDestroy {
  public readonly totalsTableConfig = {
    prefix: 'gbm.gbm-totals.',
    localeService: this.localeService,
    columns: [
      DeliveryColumnTypes.description,
      DeliveryColumnTypes.productTitle,
      DeliveryColumnTypes.unitQuantity,
      DeliveryColumnTypes.productQuantity,
    ],
    digitsInfo: {
      [DeliveryColumnTypes.unitQuantity]: '1.0-2',
      [DeliveryColumnTypes.productQuantity]: '1.0-2',
    },
  };

  public readonly filterActions: FilterAction[] = [
    {
      icon: 'file_download',
      viewValue: 'Excel',
      value: 'xlsx',
    },
    {
      icon: 'file_download',
      viewValue: 'PDF',
      value: 'pdf',
    },
    {
      icon: 'file_download',
      viewValue: 'CSV',
      value: 'csv',
    },
  ];

  public readonly headings = this.deliveryDataService.getHeadings(
    this.totalsTableConfig.columns,
    'gbm.gbm-totals.',
  );

  public readonly exportFilename = `${this.localeService.translate('gbm.gbm-totals.fileName')}`;

  public data$: Observable<HierarchicalData[]>;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly store: Store<RootState>,
    private readonly localeService: LocaleService,
    private readonly settingsService: SettingsService,
    private readonly deliveryDataService: DeliveryDataService,
  ) {}

  ngOnInit(): void {
    this.data$ = this.store
      .select(selectDeliveryTotals(this.totalsTableConfig))
      .pipe(takeUntil(this.destroy$));
  }

  ngOnDestroy(): void {
    this.store.dispatch(clearDeliveryTotalsRequest());

    this.destroy$.next();
    this.destroy$.complete();
  }

  public loadData(): void {
    this.store.dispatch(loadDeliveryTotalsRequest({ category: DeliveryCategories.gbm }));
  }

  public printReport = () =>
    combineLatest([
      this.settingsService.getSettings({
        contextName: settingsContextName,
        storage: SettingsStorage.PlatformSession,
        key: SettingsKeys.dateRange,
      }),
      this.settingsService.getSettings({
        contextName: settingsContextName,
        storage: SettingsStorage.PlatformSession,
        key: SettingsKeys.locations,
      }),
      this.store.select(UserSelectors.selectLocations),
      this.store.select(UserSelectors.selectCustomer),
      this.data$,
    ])
      .pipe(
        take(1),
        map(([dateRange, filterLocationsResult, userLocations, customer, data]) => {
          const startDate = new Date((dateRange?.[0].data as { startDate: string })?.startDate);
          const endDate = new Date((dateRange?.[0].data as { endDate: string })?.endDate);
          const filterLocations = (filterLocationsResult?.[0]?.data as number[]) ?? [];

          this.store.dispatch(
            printDeliveryReportRequest({
              report: {
                title: this.localeService.translate('gbm.title-totals'),
                subTitle: `${this.localeService.translate(
                  'gbm.report.sub-title',
                )} ${this.deliveryDataService.formatDateTime(new Date())}`,
                fileName: this.deliveryDataService.formatExportFileName(
                  this.localeService.translate('gbm.gbm-totals.fileName'),
                  customer.externalId,
                  startDate,
                  endDate,
                ),
                legend: {
                  [this.localeService.translate('gbm.report.legend.customer')]:
                    `${customer.name} (${customer.externalId})`,
                  [this.localeService.translate('gbm.report.legend.locations')]:
                    this.deliveryDataService.formatLocations(
                      userLocations.filter((userLocation) =>
                        filterLocations?.length > 0
                          ? filterLocations.includes(userLocation.id)
                          : true,
                      ),
                    ),
                  [this.localeService.translate('gbm.report.legend.start-date')]:
                    this.deliveryDataService.formatDateTime(startDate),
                  [this.localeService.translate('gbm.report.legend.end-date')]:
                    this.deliveryDataService.formatDateTime(endDate),
                },
                tableHeadings:
                  this.totalsTableConfig?.columns?.map((column) =>
                    this.localeService.translate(`${this.totalsTableConfig.prefix}${column}`),
                  ) ?? [],
                tableFooter: [
                  this.localeService.translate('gbm.report.disclaimer1'),
                  this.localeService.translate('gbm.report.disclaimer2'),
                ],

                contactInformation: {
                  email: this.localeService.translate('gbm.report.contact.email'),
                  phoneNumber: this.localeService.translate('gbm.report.contact.phone-number'),
                },
                rows: this.deliveryDataService.mapHierarchicalDataToPdfRows(data),
              },
            }),
          );
        }),
      )
      .subscribe();
}
